import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Mic, MicOff, Pause, Play, Square, Loader2, Save, X, Keyboard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { enqueueNote, newOfflineId } from '../lib/voiceQueue';
import SmartApprovalModal from './SmartApprovalModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_SECONDS = 180; // 3-minute limit

const SR =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;
const SPEECH_SUPPORTED = !!SR;

function fmt(s) {
  const m = Math.floor(s / 60);
  const sec = String(s % 60).padStart(2, '0');
  return `${m}:${sec}`;
}

/**
 * VoiceRecorder — capture a quick voice note using the device's FREE built-in
 * speech-to-text (Web Speech API). No audio is uploaded and no paid/LLM-key
 * service is used. Falls back to manual typing when dictation isn't supported.
 *
 * Props:
 *   variant : 'fab' | 'button'           (default 'button')
 *   context : { linkedClientId?, linkedAnimalId?, linkedBookingId? }
 *   onSaved : callback after a note is saved/approved
 */
export default function VoiceRecorder({ variant = 'button', context = {}, onSaved }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [text, setText] = useState('');
  const [interim, setInterim] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [saving, setSaving] = useState(false);

  const [approvalNote, setApprovalNote] = useState(null);
  const [approvalOpen, setApprovalOpen] = useState(false);

  const recognitionRef = useRef(null);
  const timerRef = useRef(null);
  const recordingRef = useRef(false);
  const pausedRef = useRef(false);

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const stopRecognition = () => {
    recordingRef.current = false;
    pausedRef.current = false;
    try { recognitionRef.current && recognitionRef.current.stop(); } catch { /* noop */ }
    stopTimer();
    setRecording(false);
    setPaused(false);
    setInterim('');
  };

  const resetAll = () => {
    stopRecognition();
    setText('');
    setInterim('');
    setSeconds(0);
  };

  useEffect(() => () => stopRecognition(), []);

  const ensureRecognition = () => {
    if (!SPEECH_SUPPORTED) return null;
    if (recognitionRef.current) return recognitionRef.current;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    rec.onresult = (e) => {
      let finalChunk = '';
      let interimChunk = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalChunk += t;
        else interimChunk += t;
      }
      if (finalChunk) {
        setText((prev) => (prev ? `${prev} ${finalChunk.trim()}` : finalChunk.trim()));
        setInterim('');
      } else {
        setInterim(interimChunk);
      }
    };
    rec.onend = () => {
      // Web Speech often auto-ends; restart while actively recording.
      if (recordingRef.current && !pausedRef.current) {
        try { rec.start(); } catch { /* already started */ }
      }
    };
    rec.onerror = (ev) => {
      if (ev.error === 'not-allowed' || ev.error === 'service-not-allowed') {
        toast.error('Microphone blocked — type your note instead');
        recordingRef.current = false;
        setRecording(false);
        stopTimer();
      }
    };
    recognitionRef.current = rec;
    return rec;
  };

  const startTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) { stopRecognition(); toast.message('3-minute limit reached'); return MAX_SECONDS; }
        return s + 1;
      });
    }, 1000);
  };

  const startRecording = () => {
    if (!SPEECH_SUPPORTED) { toast.message('Dictation not supported here — type your note'); return; }
    const rec = ensureRecognition();
    if (!rec) return;
    recordingRef.current = true;
    pausedRef.current = false;
    try { rec.start(); } catch { /* already running */ }
    setRecording(true);
    setPaused(false);
    startTimer();
  };

  const pauseRecording = () => {
    pausedRef.current = true;
    try { recognitionRef.current && recognitionRef.current.stop(); } catch { /* noop */ }
    stopTimer();
    setPaused(true);
    setInterim('');
  };

  const resumeRecording = () => {
    const rec = ensureRecognition();
    if (!rec) return;
    pausedRef.current = false;
    recordingRef.current = true;
    try { rec.start(); } catch { /* noop */ }
    setPaused(false);
    startTimer();
  };

  const handleSave = async () => {
    const trimmed = text.trim();
    if (!trimmed) { toast.error('Nothing to save yet'); return; }
    stopRecognition();
    setSaving(true);
    const offlineId = newOfflineId();
    const payload = {
      transcription: trimmed,
      visibility: 'PRIVATE',
      clientOfflineId: offlineId,
      ...(context.linkedClientId ? { linkedClientId: context.linkedClientId } : {}),
      ...(context.linkedAnimalId ? { linkedAnimalId: context.linkedAnimalId } : {}),
      ...(context.linkedBookingId ? { linkedBookingId: context.linkedBookingId } : {}),
    };
    try {
      if (!navigator.onLine) throw new Error('offline');
      const { data } = await axios.post(`${API_URL}/api/dashboard/voice-notes`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpen(false);
      resetAll();
      setApprovalNote(data);
      setApprovalOpen(true);
    } catch (err) {
      // Offline or network error — queue for later sync.
      const status = err?.response?.status;
      if (!navigator.onLine || !status || status >= 500) {
        enqueueNote(payload);
        toast.success('Saved offline — will sync when you reconnect');
        setOpen(false);
        resetAll();
        onSaved?.();
      } else {
        toast.error(err.response?.data?.detail || 'Could not save note');
      }
    } finally {
      setSaving(false);
    }
  };

  const closeRecorder = (o) => {
    if (!o) { stopRecognition(); }
    setOpen(o);
  };

  const Trigger = variant === 'fab' ? (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
      aria-label="Record voice note"
      data-testid="voice-recorder-fab"
    >
      <Mic className="h-6 w-6" />
    </button>
  ) : (
    <Button variant="outline" onClick={() => setOpen(true)} data-testid="voice-recorder-btn">
      <Mic className="h-4 w-4 mr-1" /> New voice note
    </Button>
  );

  return (
    <>
      {Trigger}

      <Dialog open={open} onOpenChange={closeRecorder}>
        <DialogContent className="sm:max-w-md" data-testid="voice-recorder-dialog">
          <DialogHeader>
            <DialogTitle>Record a voice note</DialogTitle>
            <DialogDescription>
              {SPEECH_SUPPORTED
                ? 'Speak and it turns into text on your device — free, private, no internet needed for typing.'
                : 'Dictation isn\u2019t available in this browser. Type your note below.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {SPEECH_SUPPORTED && (
              <div className="flex items-center justify-between rounded-md border p-3">
                <div className="flex items-center gap-2">
                  {recording && !paused ? (
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
                    </span>
                  ) : (
                    <Mic className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="text-sm font-medium" data-testid="recorder-timer">{fmt(seconds)}</span>
                  <span className="text-xs text-muted-foreground">/ 3:00</span>
                </div>
                <div className="flex items-center gap-2">
                  {!recording && (
                    <Button size="sm" onClick={startRecording} data-testid="recorder-start-btn">
                      <Mic className="h-4 w-4 mr-1" /> Start
                    </Button>
                  )}
                  {recording && !paused && (
                    <Button size="sm" variant="outline" onClick={pauseRecording} data-testid="recorder-pause-btn">
                      <Pause className="h-4 w-4 mr-1" /> Pause
                    </Button>
                  )}
                  {recording && paused && (
                    <Button size="sm" onClick={resumeRecording} data-testid="recorder-resume-btn">
                      <Play className="h-4 w-4 mr-1" /> Resume
                    </Button>
                  )}
                  {recording && (
                    <Button size="sm" variant="outline" onClick={stopRecognition} data-testid="recorder-stop-btn">
                      <Square className="h-4 w-4 mr-1" /> Stop
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                <Keyboard className="h-3 w-3" /> Transcript (editable)
              </div>
              <Textarea
                value={interim ? `${text}${text ? ' ' : ''}${interim}` : text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                placeholder="Your words appear here as you speak — or just type."
                data-testid="recorder-text"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => closeRecorder(false)} data-testid="recorder-cancel-btn">
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !text.trim()} data-testid="recorder-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Save & review
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <SmartApprovalModal
        note={approvalNote}
        open={approvalOpen}
        onOpenChange={setApprovalOpen}
        onApproved={onSaved}
      />
    </>
  );
}
